import * as DialogPrimitive from '@radix-ui/react-dialog';
import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

export type RecommendedSetupChanges = {
  toEnable: string[];
  toDisable: string[];
  memoryAdded: { toolName: string; keys: string[] }[];
  memoryReplaced: { toolName: string; keys: string[] }[];
  disableFutureTools: boolean;
  summaryKey: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  changes: RecommendedSetupChanges;
  /** Portal target so `absolute inset-0` covers the whole sheet, not the scroll pane it is rendered in. */
  containerNode?: HTMLElement | null;
  /** The button that opened this dialog; Radix cannot restore focus without a `DialogTrigger`. */
  triggerRef?: RefObject<HTMLButtonElement | null>;
};

// Keep the preview readable for servers with large tool catalogs.
const MAX_CHIPS = 6;

const Chips = ({ names, testId }: { names: string[]; testId: string }) => {
  const { t } = useTranslation('remote_mcp');
  const shown = names.slice(0, MAX_CHIPS);
  const rest = names.length - shown.length;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={testId}>
      {shown.map((n) => (
        <span
          key={n}
          className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
        >
          {n}
        </span>
      ))}
      {rest > 0 && (
        <span className="text-xs text-muted-foreground">
          {t('tools.confirm.and_more', { count: rest })}
        </span>
      )}
    </div>
  );
};

/** Preview recommended changes inside the Remote MCP sheet. */
export const RecommendedSetupDialog = ({
  open,
  onOpenChange,
  onConfirm,
  changes,
  containerNode,
  triggerRef,
}: Props) => {
  const { t } = useTranslation('remote_mcp');
  const { toEnable, toDisable, memoryAdded, memoryReplaced, disableFutureTools, summaryKey } =
    changes;
  const nothingToDo =
    toEnable.length === 0 &&
    toDisable.length === 0 &&
    memoryAdded.length === 0 &&
    memoryReplaced.length === 0 &&
    !disableFutureTools;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal container={containerNode || undefined}>
        <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 z-20 bg-black/30 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="absolute inset-0 z-30 flex items-center justify-center p-0 focus:outline-none"
          role="alertdialog"
          aria-modal="true"
          // Keep Escape scoped to this nested dialog.
          onEscapeKeyDown={(event) => event.stopPropagation()}
          onCloseAutoFocus={(event) => {
            if (!triggerRef?.current) return;
            event.preventDefault();
            triggerRef.current.focus();
          }}
          data-testid="remote-mcp-recommended-dialog"
        >
          <div className="max-h-[85%] w-full max-w-md space-y-4 overflow-y-auto rounded-lg bg-card p-6 shadow-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('tools.confirm.title')}</AlertDialogTitle>
            </AlertDialogHeader>
            {summaryKey && <AlertDialogDescription>{t(summaryKey)}</AlertDialogDescription>}

            <div className="space-y-3">
              {nothingToDo && (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="remote-mcp-recommended-noop"
                >
                  {t('tools.confirm.no_changes')}
                </p>
              )}

              {toEnable.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('tools.confirm.turn_on', { count: toEnable.length })}
                  </p>
                  <Chips names={toEnable} testId="remote-mcp-recommended-enable" />
                </div>
              )}

              {toDisable.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('tools.confirm.turn_off', { count: toDisable.length })}
                  </p>
                  <Chips names={toDisable} testId="remote-mcp-recommended-disable" />
                </div>
              )}

              {disableFutureTools && (
                <p
                  className="text-sm font-medium text-foreground"
                  data-testid="remote-mcp-recommended-future-off"
                >
                  {t('tools.confirm.future_tools_off')}
                </p>
              )}

              {memoryAdded.map(({ toolName, keys }) => (
                <div key={toolName}>
                  <p className="text-sm font-medium text-foreground">
                    {t('tools.confirm.memory_set', { count: keys.length, tool: toolName })}
                  </p>
                  <Chips names={keys} testId={`remote-mcp-recommended-memory-${toolName}`} />
                </div>
              ))}

              {memoryReplaced.length > 0 && (
                <div
                  className="rounded-lg bg-warning-muted p-3"
                  data-testid="remote-mcp-recommended-replace-warning"
                >
                  <p className="text-sm font-medium text-foreground">
                    {t('tools.confirm.memory_replaced_title')}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('tools.confirm.memory_replaced_body', {
                      tools: memoryReplaced.map((m) => m.toolName).join(', '),
                    })}
                  </p>
                  {memoryReplaced.map(({ toolName, keys }) => (
                    <div key={toolName} className="mt-2">
                      <p className="text-xs font-medium text-foreground">
                        {t('tools.confirm.memory_set', { count: keys.length, tool: toolName })}
                      </p>
                      <Chips names={keys} testId={`remote-mcp-recommended-replace-${toolName}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground">{t('tools.confirm.not_saved_yet')}</p>

            <Separator />
            <AlertDialogFooter>
              <Button variant="secondary" size="md" onClick={() => onOpenChange(false)}>
                {t('tools.confirm.cancel')}
              </Button>
              <Button
                variant="dark"
                size="md"
                disabled={nothingToDo}
                onClick={onConfirm}
                data-testid="remote-mcp-recommended-apply"
              >
                {t('tools.confirm.apply')}
              </Button>
            </AlertDialogFooter>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
